/**
 * "Download PDF" on a ticket (operator Reports (a), migration 0241): the run ticket the hauler's
 * load emails attach, rendered on request. The API returns a short-lived link, which opens in a
 * new tab (as the hauler invoice download in web-billing).
 *
 * On every plan that sees tickets. The PDF carries the driver's name and signature, as the paper
 * ticket does, but never the driver's notes.
 */

import React from 'react';
import { ApiError } from '@lodex-pro/web-shared';
import { usePortalTicketPdf } from '../hooks/useOperatorPortal';

export const TICKET_PDF_UNAVAILABLE_MESSAGE =
  'The ticket PDF can’t be made right now. Try again in a few minutes.';
export const TICKET_PDF_RATE_LIMITED_MESSAGE =
  'Too many downloads in a minute. Wait a moment and try again.';
export const TICKET_PDF_FAILED_MESSAGE = 'Couldn’t make the ticket PDF. Try again.';

function messageFor(err: Error): string {
  if (err instanceof ApiError && err.code === 'OPERATOR_TICKET_PDF_UNAVAILABLE') {
    return TICKET_PDF_UNAVAILABLE_MESSAGE;
  }
  if (err instanceof ApiError && err.code === 'RATE_LIMIT_EXCEEDED') {
    return TICKET_PDF_RATE_LIMITED_MESSAGE;
  }
  return TICKET_PDF_FAILED_MESSAGE;
}

export function DownloadTicketPdf({ haulId }: { haulId: string }): React.JSX.Element {
  const pdf = usePortalTicketPdf(haulId);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}>
      <button
        type="button"
        disabled={pdf.isPending}
        onClick={(): void => {
          pdf.mutate(undefined, {
            onSuccess: (link) => {
              window.open(link.url, '_blank', 'noopener');
            },
          });
        }}
        style={{
          padding: '6px 14px',
          borderRadius: '10px',
          border: '1px solid var(--color-border-secondary)',
          background: 'var(--color-bg-tertiary)',
          color: 'var(--color-text-primary)',
          fontSize: '13px',
          cursor: pdf.isPending ? 'wait' : 'pointer',
        }}
      >
        {pdf.isPending ? 'Making PDF…' : 'Download PDF'}
      </button>
      {pdf.isError && (
        <span role="alert" style={{ color: 'var(--color-danger)', fontSize: '13px' }}>
          {messageFor(pdf.error)}
        </span>
      )}
    </div>
  );
}
